import _ from 'lodash';
import React, { Suspense, useContext, useEffect, useState } from 'react';
import { Sidebar } from 'semantic-ui-react';
import { FrameworkContext } from '../../cnr/contexts/FrameworkContent';
import { GoogleSheetsContext } from '../../cnr/contexts/GoogleSheetsContext';
import { ParentContext } from '../../cnr/contexts/ParentContext';
import { UiItemContext } from '../../cnr/contexts/UiItemContext';
import { googleModeTypes } from '../../cnr/reducers/GoogleSheetsReducer';
import { _animateTypes } from '../../motions/AnimateComponent';
import FullPageWrapper from '../../wrappers/FullPageWrapper';
import TransitionWrapper from '../../wrappers/TransitionWrapper';
import Wrapper, { wrapperTypes } from '../../wrappers/Wrapper';
import SuspenseDimmer from '../alerts/SuspenseDimmer';
import UiSaveButtons from '../buttons/UiSaveButtons';
import MenuVert from '../menus/MenuVert';
import DataSidebar from '../swipers/DataSidebar';
import AppDashboardContent from './AppDashboardContent';
import { GolfParentContext } from 'projectSpecific/golf/cnr/contexts/GolfParentContext';
import ItemMenuSquares, { _imsTypes } from 'global/wrappers/ItemMenuSquares';

export const allDataModes = {
  allClientAppUsers: 'allClientAppUsers',
  appCheck: 'appCheck',
  appData: 'appData',
  appDatas: 'appDatas',
  appDocuments: 'appDocuments',
  appErrors: 'appErrors',
  appFunctionSettings: 'appFunctionSettings',
  appManifest: 'appManifest',
  appNotifications: 'appNotifications',
  appSettings: 'appSettings',
  appTransfer: 'appTransfer',
  appUsers: 'appUsers',
  clientAppUsers: 'clientAppUsers',
  clientProfiles: 'clientProfiles',
  createClient: 'createClient',
  createEvent: 'createEvent',
  dataConstraints: 'dataConstraints',
  deleteEvent: 'deleteEvent',
  fileManagement: 'fileManagement',
  googleDashboard: 'googleDashboard',
  googleRegistration: 'googleRegistration',
  imageMapping: 'imageMapping',
  import: 'import',
  linkData: 'linkData',
  linkNotifications: 'linkNotifications',
  locationMapping: 'locationMapping',
  mergeData: 'mergeData',
  moveDataTo: 'moveDataTo',
  quickView: 'quickView',
  recreateEvent: 'recreateEvent',
  regDashboard: 'regDashboard',
  regData: 'regData',
  registrationData: 'registrationData',
  storageManagement: 'storageManagement',
  testMode: 'testMode',
  transferAppSettings: 'transferAppSettings',
  transferManifests: 'transferManifests',
  transferSettings: 'transferSettings',
  zoneMapping: 'zoneMapping',
}

export const _allDataModes_base = {
  [allDataModes.appCheck]: allDataModes.appCheck,
  [allDataModes.appErrors]: allDataModes.appErrors,
  [allDataModes.appManifest]: allDataModes.appManifest,
  [allDataModes.appSettings]: allDataModes.appSettings,
  [allDataModes.appData]: allDataModes.appData,
  [allDataModes.appDatas]: allDataModes.appDatas,
  [allDataModes.appDocuments]: allDataModes.appDocuments,
  [allDataModes.appTransfer]: allDataModes.appTransfer,
  [allDataModes.appUsers]: allDataModes.appUsers,
}

export const allDataModes_home = {
  [allDataModes.appFunctionSettings]: allDataModes.appFunctionSettings,
}

export const allDataModes_client = {
  [allDataModes.createEvent]: allDataModes.createEvent,
  [allDataModes.regDashboard]: allDataModes.regDashboard,
}

export const allDataModes_event = {
  [allDataModes.appData]: allDataModes.appData,
  [allDataModes.appNotifications]: allDataModes.appNotifications,
  [allDataModes.appSettings]: allDataModes.appSettings,
  [allDataModes.appUsers]: allDataModes.appUsers,
  [allDataModes.dataConstraints]: allDataModes.dataConstraints,
  [allDataModes.fileManagement]: allDataModes.fileManagement,
  [allDataModes.googleDashboard]: allDataModes.googleDashboard,
  [allDataModes.linkData]: allDataModes.linkData,
  [allDataModes.linkNotifications]: allDataModes.linkNotifications,
  [allDataModes.locationMapping]: allDataModes.locationMapping,
  [allDataModes.mergeData]: allDataModes.mergeData,
  [allDataModes.recreateEvent]: allDataModes.recreateEvent,
  [allDataModes.regDashboard]: allDataModes.regDashboard,
  [allDataModes.deleteEvent]: allDataModes.deleteEvent,
}

export const appViewerColors = {
  dmv: 'black',
  gsv: 'blue',
  fsv: 'black'
}

const _caption = 'Dashboard'

/**
 * This is the overall dataViewer for the app
 * @param {object} props 
 * @returns AppDataViewer, GoogleSheetsViewer or RegDataViewer
 * @contexts (GoogleSheetsContext, RegDataContext)
 */
const AppDashboard = (props) => {

  const { noModal } = props ?? {}

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states } = parentContext ?? {}
  const { appSettings_state, currentSettings_state, paps_state, transition_state } = states ?? {}
  const { selectedDataMode } = appSettings_state ?? {}
  const { pathViews } = paps_state ?? {}
  const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
  const { googleSheets } = aps_global ?? {}
  const { googleSheetsId: googleSheetsId_existing } = googleSheets ?? {}

  const isEvent = pathViews.events ? true : false
  const isClient = pathViews.clients && !isEvent

  const { transitions } = transition_state ?? {}
  const transition = transitions ? transitions[_animateTypes.component] : null
  const { showTransition } = transition ?? {}

  // frameworkContext
  const frameworkContext = useContext(FrameworkContext);
  const { framework_state } = frameworkContext ?? {}
  const { selectedDataMode: selectedDataMode_fw } = framework_state ?? {}

  const _selectedDataMode = selectedDataMode_fw ? selectedDataMode_fw : selectedDataMode

  // uiItemContext - will not exist if from landing page
  const uiItemContext = useContext(UiItemContext);
  const { item_handlers, item_state } = uiItemContext ?? {}
  const { handleClose_openedItem } = item_handlers ?? {}

  // googleSheetsContext
  const googleSheetsContext = useContext(GoogleSheetsContext)
  const { googleSheets_handlers } = googleSheetsContext ?? {}

  const soloItem = item_state ? true : false

  // local states
  const [captions, setCaptions] = useState({ topper: _caption, topper2: 'App Data!!!' })
  const [activeDashboardMenuItems, setActiveDashboardMenuItems] = useState({})
  const [showSideMenu, setShowSideMenu] = useState()
  const [currentDataMode, setCurrentDataMode] = useState()

  // get the menuItems that will appear in the side menu
  useEffect(() => {
    let _menuItems = {}
    if (soloItem) {
      _menuItems[allDataModes.appData] = allDataModes.appData
    } else {
      _menuItems = _allDataModes_base
      // _menuItems = { ..._menuItems, ..._allDataModes_base }
      // if (isEvent) {
      //   _menuItems = { ..._menuItems, ...allDataModes_event }
      // }
      // if (isClient) {
      //   _menuItems = { ..._menuItems, ...allDataModes_client }
      // }
    }
    // the active dataMode will be appData, unless _selectedDataMode has a value 
    setCurrentDataMode(_selectedDataMode ? _selectedDataMode : allDataModes.appSettings)
    setCaptions({ topper: _caption, topper2: _.startCase(_selectedDataMode ? _selectedDataMode : allDataModes.appData) })
    setActiveDashboardMenuItems(_menuItems)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleSheetsId_existing, _selectedDataMode]);

  const handleCancel = () => {
    props.handleCancel && props.handleCancel()
    googleSheets_handlers && googleSheets_handlers.handleSet_googleMode(googleModeTypes.appData)
    if (handleClose_openedItem) { handleClose_openedItem() }
  }

  const handleShowSideMenu = () => setShowSideMenu(!showSideMenu)

  /**
   * changes the dataMode for hte componnent
   * @param {object} adm 
   */
  const handleDataMode = (adm) => {
    if (_selectedDataMode) {
      handleCancel()
      setShowSideMenu()
    } else {
      const _adm = adm ? adm : allDataModes.appData
      setCurrentDataMode(_adm)
      setCaptions({ topper: _caption, topper2: _.startCase(_adm) })
      setShowSideMenu()
    }
  }

  /**
   * 
   * @returns one button that opens the dashboard side menu
   */
  const buttons_mode = () => {
    const btns = [{ caption: _.startCase(currentDataMode), oc: handleShowSideMenu, icon: 'unordered list' }]
    return <UiSaveButtons
      others={btns}
      color={'black'}
    />
  }

  /**
   * 
   * @returns a wrapper containing a header and content for the dashboard.
   * The header contains a button to apend the menu containing the activeDashboardMenuItems.
   * 
   */
  const wrapper_content = () => <Suspense fallback={<SuspenseDimmer origin={'AppDashboard'} />}><Wrapper
    header={!selectedDataMode_fw && buttons_mode()}
    content={currentDataMode && <AppDashboardContent currentDataMode={currentDataMode} handleDataMode={handleDataMode} />}
    wrapperType={wrapperTypes.paddedHeaderAndFooter}
    css={{ header: 'header-flex', container: 'wrp' }}
  /></Suspense>

  /**
   * 
   * @returns a list of the activeDashboardMenuItems
   */
  const menu_left = () => <TransitionWrapper>
    {/* <ItemMenuSquares menuItems={_.sortBy(activeDashboardMenuItems)} imsType={_imsTypes.inline} onClick={handleDataMode} /> */}
    <MenuVert
      header={'Options'}
      menuItems={_.sortBy(activeDashboardMenuItems)}
      activeMenuItem={currentDataMode}
      useIcons={true}
      oc={handleDataMode}
      asSquares={true}
    />
  </TransitionWrapper>

  const sidebar_left = (visible) => <DataSidebar direction={'left'} visible={visible} content={visible && menu_left()} onHide={setShowSideMenu} />

  /**
   * 
   * @returns a sidebar containing the activeDashboardMenuItems and
   * The content, depending on the currentDataMode
   */
  const sidebar = () => <Sidebar.Pushable style={{ overflow: 'hidden' }}>
    {sidebar_left(showSideMenu ? true : false)}
    <Sidebar.Pusher dimmed={showSideMenu ? true : false} className={'h100'}>
      {wrapper_content()}
    </Sidebar.Pusher>
  </Sidebar.Pushable>

  const fullPageWrapper = () => <FullPageWrapper
    content={sidebar()}
    handleCancel={handleCancel}
    topperCaption={captions.topper}
    topperCaption2={captions.topper2}
    fullMode={true}
    noModal={noModal}
    css={{ container: 'app-dashboard app-dark-mode' }}
    ignoreStyle={true}
  />

  if (showTransition) {
    return fullPageWrapper()
  } else {
    return fullPageWrapper()
  }

}

export default AppDashboard